/** What the film player reads (docs/SPEC.md §19, §25.4): the manifest of a
 *  version and each scene's sidecar (version 4), as
 *  `sdk/truereel_film/manifest.py` and `builders/player.py` write them.
 *
 *  Axes: every vector of a sidecar is in the film's axes (Z up, the front
 *  −Y, metres), as `film.json` has them; `world.ts` turns them into three's.
 *  Nothing here imports three. */
import type { HeldRow, OverrideRow } from "./hold";
import type { Patch } from "./patches";

export type Vec3 = [number, number, number];
export type Aspect = "9:16" | "1:1" | "16:9";
export type TransitionKind = "cut" | "fade" | "wipe" | "carry";
export type WipeDirection = "left" | "right" | "up" | "down";

/** `[t, from, to, fov_y]`: a camera at local time `t`, the vertical field
 *  of view in degrees. */
export type CameraRow = [number, Vec3, Vec3, number];
/** `[t, position, yaw, scale, pose_amount]`: an object at local time `t`
 *  (its base centre, degrees about Z, its uniform scale, its pose 0–1). */
export type ObjectRow = [number, Vec3, number, number, number];
/** `[t, position, yaw, scale]`: a person's root at local time `t` (the
 *  point between the feet on the ground, degrees about Z, its scale); the
 *  pose is not sampled but evaluated from the clip (P29). */
export type PersonRow = [number, Vec3, number, number];

export interface Caption {
  id: string;
  text: string;
  style: "headline" | "body" | "kicker" | "end-card";
  t0: number;
  t1: number;
  anchor: string;
  color: string;
  enter: string;
  exit: string;
}

export interface ManifestScene {
  id: string;
  name: string;
  seconds: number;
  /** Where its `t = 0` is on the film's clock, seconds. */
  start: number;
  first_frame: number;
  frames: number;
  /** Baked and playable (§6.2, P28-T06): a version still building answers
   *  the scenes baked so far; the others have no URLs yet. */
  ready: boolean;
  glb_url: string | null;
  sidecar_url: string | null;
  captions: Caption[];
}

export interface ManifestTransition {
  kind: TransitionKind;
  /** What it takes out of the film's clock; 0 for a cut. */
  seconds: number;
  color: string | null;
  direction: WipeDirection | null;
  /** A carry's camera on its own clock, 0 to `seconds`, relative to where
   *  the cast member it follows stands: `anchors[0]` in the outgoing scene,
   *  `anchors[1]` in the incoming one (the origin when it follows none). */
  camera: {
    member: string | null;
    anchors: [Vec3, Vec3];
    samples: CameraRow[];
  } | null;
}

export interface Manifest {
  version: 1;
  film_id: string;
  film_version: number;
  title: string;
  aspect: Aspect;
  fps: number;
  length_s: number;
  frames: number;
  look: {
    palette: Record<"primary" | "accent" | "background" | "text", string>;
  } & Record<string, unknown>;
  leader: { seconds: number; color: string };
  trailer: { seconds: number; color: string };
  scenes: ManifestScene[];
  transitions: ManifestTransition[];
  sound_url: string | null;
  /** What the player lays over bakes older than the version (an instant
   *  change keeps its parent's bakes, P22-T06); none when the bakes are the
   *  version's own. */
  patches?: Patch[];
}

export interface SidecarLight {
  name: string;
  type: "area" | "point" | "spot" | "sun";
  position: Vec3;
  aim: Vec3;
  color: string;
  /** Cycles watts (a sun's W/m²). */
  intensity: number;
  size: [number, number];
}

export type SidecarEnvironment =
  | { kind: "color"; color: string; strength: number }
  | {
      kind: "gradient";
      top: string;
      horizon: string;
      bottom: string;
      strength: number;
    }
  | {
      kind: "hdri";
      name: string;
      file: string;
      strength: number;
      rotation: number;
    };

export interface SidecarObject {
  id: string;
  node: string;
  asset: string;
  kind: string;
  ref: string;
  role: string | null;
  model: string;
  on: string;
  motion: string | null;
  box: { min: Vec3; max: Vec3 };
  pose: { name: string; node: string; axis: Vec3; degrees: number } | null;
  /** The part a person turns by `crankDeg` of its own clip numbers (P29:
   *  an object has `pose` or `crank`, never both). */
  crank: {
    node: string;
    axis: Vec3;
    person: string;
    followers?: string[];
  } | null;
  samples: ObjectRow[];
  /** P30: the hold, whose rows place the object inside its window. */
  held: SidecarHeld | null;
  /** P30: the slots whose artwork is a screen sequence. */
  screens: SidecarScreen[];
}

/** A placed object in a person's hand (P30, §P30.1 (6)): who holds it, in
 *  which hand, the window, and its full transform at the window's rows
 *  (`[t, position, quaternion, scale]`, film axes): `from_t`, every frame
 *  strictly inside, the end (`to_t` or the scene's seconds). The player
 *  places the object from them (`heldAt`); outside, from its `samples`. */
export interface SidecarHeld {
  person: string;
  hand: "left" | "right" | "both";
  from_t: number;
  to_t: number | null;
  samples: HeldRow[];
}

/** A slot whose artwork is a screen sequence (P30): the material it wears
 *  (`<object>:<slot>`) and, per item, the time it shows from and the
 *  texture of `scene.glb` that shows it (item 0 the material's own map).
 *  At `t` the last item whose `at_t` has come is shown (`sequenceItem`). */
export interface SidecarScreen {
  slot: string;
  material: string;
  items: { at_t: number; texture: number }[];
}

/** A clip laid over a person's own by a bone mask (P30, §P30.3 (1)): a
 *  gesture (`arm`, its clip time from `from_t`) or a finger pose
 *  (`fingers`, its clip time `pressTau(t, presses)`); every bone of
 *  `bones` takes `slerp(base, layer, layerWeight(t, …))`. */
export interface SidecarLayer {
  kind: "arm" | "fingers";
  /** The clip's ref (`actions[].ref`) and its glTF animation. */
  clip: string;
  animation: string;
  bones: string[];
  seconds: number;
  loop: boolean;
  from_t: number;
  to_t: number;
  blend_in: number;
  blend_out: number;
  presses: number[] | null;
}

/** An override (P30, §P30.3 (4)): a reach to a grip, or the left arm on a
 *  two-hand object — the three bones' local rotations (glTF axes) and a
 *  weight per row, `[t, w, q_upper, q_lower, q_hand]`, applied after the
 *  layers (`ikAt`); or the lean of a reach (2026-10-24 fix(people) reach and
 *  stages) on the hips, the spine, the chest and the upper legs. */
export interface SidecarOverride {
  side: "l" | "r";
  bones: string[];
  from_t: number;
  to_t: number;
  samples: OverrideRow[];
}

/** A figure the scene's people wear: its hidden template node under
 *  `figures` (`figure:<slug>` → `figure:<slug>:asset` → the skinned body),
 *  cloned per person by the player (P29). */
export interface SidecarFigure {
  slug: string;
  ref: string;
  node: string;
  triangles: number;
}

/** A clip the scene's people play: the glTF animation `action:<slug>`,
 *  whose tracks target the action's own skeleton nodes. */
export interface SidecarAction {
  slug: string;
  ref: string;
  animation: string;
}

/** A person's own colour per slot of its figure (`null` keeps the
 *  template's material: a material look). */
export interface SidecarSlot {
  color: string | null;
  roughness: number;
  metallic: number;
}

export interface SidecarPerson {
  id: string;
  /** `person:<id>`, the clone the player makes. */
  node: string;
  /** The figure's ref (`figures[].ref`) and the clip's (`actions[].ref`). */
  figure: string;
  clip: string;
  seconds: number;
  /** The resolved loop: the person's, else the clip's. */
  loop: boolean;
  stride_m: number | null;
  cadence_hz: number | null;
  speed: number;
  from_t: number;
  phase: number;
  use: { object: string; anchor: string } | null;
  path: { at: [number, number]; t: number }[] | null;
  /** The crowd's id for an expanded person, else null. */
  crowd: string | null;
  slots: Record<string, SidecarSlot>;
  samples: PersonRow[];
  /** P30: gestures, then finger poses, in the order they apply. */
  layers: SidecarLayer[];
  /** P30: arm overrides, applied after the layers. */
  overrides: SidecarOverride[];
}

export interface FilmSidecar {
  version: 4;
  film_id: string;
  scene_id: string;
  name: string;
  aspect: Aspect;
  seconds: number;
  fps: number;
  frames: number;
  axes: "z-up";
  product: string | null;
  stage: { rig: string; nodes: string[]; extent: [number, number] };
  objects: SidecarObject[];
  /** P29: the figures and clips the scene's people use, once each, and
   *  the people (at most 24, a crowd's expanded). */
  figures: SidecarFigure[];
  actions: SidecarAction[];
  people: SidecarPerson[];
  camera: {
    view: { named: null; from: Vec3; to: Vec3; fov: number };
    move: string;
    clamped: boolean;
    samples: CameraRow[];
  };
  lights: SidecarLight[];
  environment: SidecarEnvironment;
  background: string;
  captions: Caption[];
  /** The overlay document (version 2) the captions are drawn from. */
  overlay: { version: 2; layers: unknown[] } & Record<string, unknown>;
  /** The pictures the overlay's image layers show (the look's logo over an
   *  end card), by upload id, as `data:` URLs; absent in a bake made before
   *  2026-10-16. */
  assets?: Record<string, string>;
  warnings: string[];
}

export class FilmFileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FilmFileError";
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function need(doc: Record<string, unknown>, keys: string[], what: string) {
  for (const key of keys) {
    if (!(key in doc)) throw new FilmFileError(`${what} has no "${key}"`);
  }
}

/** A manifest as the API serves it, checked for what the player relies
 *  on: the version, the scenes in order, one transition per border. */
export function parseManifest(json: unknown): Manifest {
  if (!isObject(json)) throw new FilmFileError("the manifest is not an object");
  need(
    json,
    ["version", "aspect", "fps", "scenes", "transitions", "leader", "trailer"],
    "the manifest",
  );
  if (json.version !== 1) {
    throw new FilmFileError(`manifest version ${String(json.version)}`);
  }
  const scenes = json.scenes;
  const transitions = json.transitions;
  if (!Array.isArray(scenes) || scenes.length === 0) {
    throw new FilmFileError("the manifest has no scenes");
  }
  if (!Array.isArray(transitions) || transitions.length !== scenes.length - 1) {
    throw new FilmFileError("the manifest needs one transition per border");
  }
  for (const scene of scenes) {
    if (!isObject(scene)) throw new FilmFileError("a scene is not an object");
    need(
      scene,
      ["id", "seconds", "start", "frames", "ready", "glb_url", "sidecar_url"],
      "a scene",
    );
  }
  return json as unknown as Manifest;
}

/** Every scene is baked: the whole film plays (§6.1). */
export function allReady(manifest: Manifest): boolean {
  return manifest.scenes.every((s) => s.ready);
}

/** The film of one scene of `manifest` (§6.1: a scene plays alone; a tile
 *  is drawn from it): no transition, no leader or trailer, so its film time
 *  is the scene's local time; the patches that concern it kept. */
export function sceneAlone(manifest: Manifest, sceneId: string): Manifest {
  const scene = manifest.scenes.find((s) => s.id === sceneId);
  if (!scene) throw new FilmFileError(`no scene "${sceneId}"`);
  return {
    ...manifest,
    length_s: scene.seconds,
    frames: scene.frames,
    leader: { ...manifest.leader, seconds: 0 },
    trailer: { ...manifest.trailer, seconds: 0 },
    scenes: [{ ...scene, start: 0, first_frame: 0 }],
    transitions: [],
    patches: (manifest.patches ?? []).filter(
      (p) => p.kind !== "transition" && p.scene === sceneId,
    ),
  };
}

/** A scene's sidecar, version 4 (P30: an object's `held` and `screens`, a
 *  person's `layers` and `overrides`; P29: `figures`, `actions`, `people`
 *  and an object's `crank`; nothing reads version 3). */
export function parseSidecar(json: unknown): FilmSidecar {
  if (!isObject(json)) throw new FilmFileError("the sidecar is not an object");
  if (json.version !== 4) {
    throw new FilmFileError(`sidecar version ${String(json.version)}`);
  }
  need(
    json,
    [
      "seconds",
      "fps",
      "objects",
      "figures",
      "actions",
      "people",
      "camera",
      "lights",
      "environment",
      "overlay",
    ],
    "the sidecar",
  );
  const camera = json.camera;
  if (
    !isObject(camera) ||
    !Array.isArray(camera.samples) ||
    camera.samples.length === 0
  ) {
    throw new FilmFileError("the sidecar's camera has no samples");
  }
  return json as unknown as FilmSidecar;
}
